import React, { useMemo } from 'react';

import AppShell from '@/components/AppShell';
import { useAppConfig } from '@/hooks/useAppConfig';
import useNavigation from '@/hooks/useNavigation';
import { ProductivityManagerTabsEnum } from '@/modules/productivity/types';
import routes from '@/pages/corporate/routes';

import { SidebarItems } from '../../hooks/useSidebar';

interface AdminProps {
  sidebarItems: SidebarItems;
  children: React.ReactNode;
}

const SIDEBAR_ENABLED_ROUTES = [
  routes.LearningContent(),
  routes.RecentLearningContent(),
  routes.LearningContentDraft(),
  routes.LearningContentActive(),
  routes.LearningContentArchived(),
  routes.Dashboard(),
  routes.ViewAcademy(),
  routes.Learners(),
  routes.ActiveLearners(),
  routes.PendingLearners(),
  routes.DeactivatedLearners(),
  routes.GroupManager(),
  routes.CollectionManager(),
  routes.Organization(),
  routes.Assignments(),
  routes.SkillsAndMastery(),
  routes.ScorecardManager(),
  routes.LyearnConnectManager(),
  routes.AdminCertificates(),
  routes.TagsManager(),
  routes.Staff(),
  routes.ActiveStaff(),
  routes.PendingStaff(),
  routes.DeactivatedStaff(),
  routes.Reports(),
  routes.StandardReports(),
  routes.CustomReports(),
  routes.Settings(),
  routes.AdminEvents(),
  routes.ManageUpcomingEvents(),
  routes.ManageFinishedEvents(),
  routes.ManageRecurringEvents(),
  routes.Settings(),
  routes.StaffRoleManager(),
  routes.GettingStarted(),
  routes.IntegrationsManager(),
  routes.NotificationsManager(),
  routes.CategoriesManager(),
  routes.MindsetManager(),
  routes.TaskStatusesManager(),
  routes.MetricsManager(),
  routes.AdminQuestionManager('[tab]'),
  routes.SystemAgendaManager(),
  routes.UserSurveys(),
  routes.UserSurveyManager('[type]', '[tab]'),
  routes.UserSurveysQuestionBank(),
  routes.UserSurveyActionPlans(),
  routes.JourneyManager(),
  routes.AnnouncementManager(),
  routes.GamificationManagerActive(),
  routes.GamificationManagerArchived(),
  routes.KnowledgeAssetManager('[type]'),
  routes.KnowledgeAssetManagerActive('[type]'),
  routes.KnowledgeAssetManagerPending('[type]'),
  routes.GamificationPointsActive(),
  routes.GamificationStreakActive(),
  routes.GamificationStreakArchived(),
  routes.GamificationPointsArchived(),
  routes.GamificationBadgeActive(),
  routes.GamificationBadgeArchived(),
  routes.GamificationBadgeGroupActive(),
  routes.GamificationBadgeGroupArchived(),
  routes.GamificationLeaderboardActive(),
  routes.GamificationLeaderboardArchived(),
  routes.AuditLog(),
  routes.ProductivityManagerTab('[tab]' as ProductivityManagerTabsEnum),
  routes.ProductivityManagerTab(ProductivityManagerTabsEnum.Departments),
];

// Unified shell only: content detail pages and their Reports tab keep the sidebar, like the
// learner pages they mirror (Reports is a tab of the same page). Legacy admin keeps these full-width.
const UNIFIED_SIDEBAR_ENABLED_ROUTES = [
  routes.AdminPathDetails('[pathId]'),
  routes.PathReportTab('[pathId]', '[tab]'),
  routes.AdminCourseDetails('[courseId]'),
  routes.CourseReportTab('[courseId]', '[tab]'),
  routes.AdminMicroLearningDetails('[microLearningId]'),
  routes.AdminQuizDetails('[contentId]'),
  routes.AdminQuizReports('[contentId]'),
  routes.AdminScormDetails('[contentId]'),
  routes.ScormReportTab('[contentId]', '[tab]'),
  routes.AdminExerciseDetails('[exerciseId]'),
  routes.AdminExerciseReport('[exerciseId]'),
  routes.ArticleDetails('[articleId]'),
  routes.ArticleReport('[articleId]'),
  routes.AdminExternalContentDetails('[contentId]'),
  routes.ExternalContentReport('[contentId]'),
  routes.AdminRoleplayDetails('[roleplayId]'),
  routes.AdminRoleplayReports('[roleplayId]'),
  routes.AdminEventDetails('[eventId]'),
  routes.AdminEventAttendees('[eventId]'),
  routes.AdminEventReport('[eventId]'),
];

function Admin(props: AdminProps) {
  const { sidebarItems, children } = props;
  const navigation = useNavigation();
  const {
    ui: { isUnifiedSidebar },
  } = useAppConfig();

  const showSidebarByDefault = useMemo(
    () =>
      SIDEBAR_ENABLED_ROUTES.some((route) => route === navigation.pathname) ||
      (isUnifiedSidebar &&
        UNIFIED_SIDEBAR_ENABLED_ROUTES.some((route) => route === navigation.pathname)),
    [navigation.pathname, isUnifiedSidebar],
  );

  return (
    <AppShell legacySidebarItems={sidebarItems} showSidebarByDefault={showSidebarByDefault}>
      {children}
    </AppShell>
  );
}

export default Admin;
